import { MarketLeader, MarketSet } from "@/types/cardTypes";
import { MarketStats } from "@/types/market";
import { fetchApi } from "./base";

export function getMarketLeaders() {
  return fetchApi<MarketLeader[]>("/api/market/leaders", {
    method: "GET",
  });
}

export function getMarketTopCards() {
  return fetchApi<MarketLeader[]>("/api/market/top-cards", {
    method: "GET",
  });
}

export function getMarketTopSets() {
  return fetchApi<MarketSet[]>("/api/market/top-sets", {
    method: "GET",
  });
}

export function getMarketTrendingCards() {
  return fetchApi<MarketLeader[]>("/api/cards/trending", {
    method: "GET",
  });
}

export function getMarketLive() {
  return fetchApi<MarketStats>("/api/market/live", {
    method: "GET",
  });
}

export type TruePriceEvidence = {
  id: string;
  source: string;
  type: "active_listing" | "completed_sale" | "price_guide" | "market_sale";
  title: string | null;
  url: string | null;
  itemPrice: number;
  shippingPrice: number;
  totalPrice: number;
  currency: string;
  matchConfidence: number;
  occurredAt: string | null;
  observedAt: string;
  included: boolean;
  exclusionReason: string | null;
};

export type TruePriceResult = {
  evidenceSummary?: {
    basis: string;
    guideCount: number;
    completedSales: { count: number; latestSaleAt: string | null; periods: Array<{ days: number; count: number; median: number | null }>; scope: string };
    coverage: Array<{ source: string; kind: string; observations: number; included: number; status: string }>;
  };
  status?: string;
  observedAt?: string | null;
  providerHistory?: Array<{ t: number; p: number }>;
  identity?: import("@/types/pricing").PricingSummary["identity"];
  truePrice: number | null;
  range: { low: number | null; high: number | null };
  confidence: {
    score: number;
    band: "high" | "medium" | "low";
    includedObservations: number;
    excludedObservations: number;
    sourceCount: number;
  };
  signal: {
    recommendation: "buy" | "fair" | "wait" | "insufficient_data";
    bestAvailablePrice: number | null;
    discountToTruePricePercent: number | null;
  };
  sources: Array<{
    source: string;
    observations: number;
    included: number;
    average: number | null;
    weightPercent?: number;
  }>;
  evidence: TruePriceEvidence[];
  methodology: {
    windowDays: number;
    guideWindowDays?: number;
    ebaySoldHistoryConnected?: boolean;
    identityConfidenceMinimum: number;
    completedSalesWeight: number;
    activeListingsWeight: number;
    priceGuideWeight: number;
    outliersExcluded: boolean;
    shippingIncludedWhenAvailable: boolean;
  };
  calculatedAt: string;
};

export function getCardTruePrice(
  cardId: number,
  options: {
    cardFormat?: "raw" | "graded";
    language?: "english" | "japanese" | "chinese";
    condition?: string;
    printing?: string;
    gradingCompany?: string;
    grade?: string;
    chineseScript?: "simplified" | "traditional" | null;
    labelTier?: "standard" | "pristine" | "black_label" | null;
  } = {},
) {
  return fetchApi<{ success: boolean; data: TruePriceResult }>(
    `/api/market/cards/${cardId}/true-price`,
    {
      method: "GET",
      params: {
        cardFormat: options.cardFormat ?? "raw",
        language: options.language ?? "english",
        condition: options.condition || undefined,
        printing: options.printing || undefined,
        gradingCompany: options.gradingCompany || undefined,
        grade: options.grade || undefined,
        chineseScript: options.chineseScript || undefined,
        labelTier: options.labelTier || undefined,
      },
    },
  );
}

